/**
 * PlacementsSection — campaign × channel placements inside the campaign
 * detail page. Deploy SAFETY mirrors the API: channel campaigns are
 * created PAUSED; going live is always an explicit Resume.
 */

import { useState } from 'react';
import type { AdoptPlacementDto } from '@/lib/api';
import {
  useAdoptAccounts,
  useAdoptPlacementLifecycle,
  useAdoptPlacements,
  useCreateAdoptPlacement,
  useDeployAdoptPlacement,
} from '../hooks/useAdvertisingOptimization';

interface Props {
  holderId: string;
  campaignId: string;
  dailyBudgetCeilingCents: number;
}

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  deploying: 'bg-amber-100 text-amber-800',
  paused: 'bg-amber-100 text-amber-800',
  live: 'bg-emerald-100 text-emerald-800',
  error: 'bg-rose-100 text-rose-800',
};

function CreatePlacementForm({
  holderId,
  campaignId,
  dailyBudgetCeilingCents,
  onDone,
}: Props & { onDone: () => void }) {
  const accountsQuery = useAdoptAccounts(holderId);
  const createPlacement = useCreateAdoptPlacement(holderId, campaignId);
  const [accountId, setAccountId] = useState('');
  const [dailyBudgetUsd, setDailyBudgetUsd] = useState('10');
  const [keywordsText, setKeywordsText] = useState('');
  const [matchType, setMatchType] = useState<'exact' | 'phrase' | 'broad'>('phrase');

  const activeAccounts = (accountsQuery.data ?? []).filter((a) => a.status === 'active');
  const keywords = keywordsText
    .split('\n')
    .map((k) => k.trim())
    .filter(Boolean)
    .map((text) => ({ text, matchType }));

  const submit = async (): Promise<void> => {
    await createPlacement.mutateAsync({
      channelAccountId: accountId,
      dailyBudgetCents: Math.round(Number(dailyBudgetUsd) * 100),
      keywords,
    });
    onDone();
  };

  if (!accountsQuery.isLoading && activeAccounts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No active channel account. Connect a Google Ads account in Settings first.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Channel account</span>
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">Select account…</option>
          {activeAccounts.map((a) => (
            <option key={a.channelAccountId} value={a.channelAccountId}>
              {a.descriptiveName ?? a.externalCustomerId ?? a.channelAccountId} ({a.mode})
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">
          Daily budget (USD, ceiling {centsToUsd(dailyBudgetCeilingCents)})
        </span>
        <input
          type="number"
          min={1}
          className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
          value={dailyBudgetUsd}
          onChange={(e) => setDailyBudgetUsd(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Keywords (one per line)</span>
        <textarea
          rows={4}
          className="rounded-md border border-border bg-background px-2 py-1.5 font-mono text-sm"
          value={keywordsText}
          onChange={(e) => setKeywordsText(e.target.value)}
          placeholder={'free photoshop alternative\nonline photo editor'}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm w-40">
        <span className="text-muted-foreground">Match type</span>
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5"
          value={matchType}
          onChange={(e) => setMatchType(e.target.value as typeof matchType)}
        >
          <option value="exact">exact</option>
          <option value="phrase">phrase</option>
          <option value="broad">broad</option>
        </select>
      </label>
      {createPlacement.isError && (
        <div className="text-sm text-rose-600">
          {createPlacement.error instanceof Error
            ? createPlacement.error.message
            : 'Failed to create placement'}
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => void submit()}
          disabled={!accountId || keywords.length === 0 || createPlacement.isPending}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          Create placement
        </button>
        <button onClick={onDone} className="px-3 py-1.5 rounded-md border border-border text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function PlacementRow({
  placement,
  holderId,
  campaignId,
}: {
  placement: AdoptPlacementDto;
  holderId: string;
  campaignId: string;
}) {
  const deploy = useDeployAdoptPlacement(holderId, campaignId);
  const lifecycle = useAdoptPlacementLifecycle(holderId, campaignId);
  const keywordCount = placement.targeting?.keywords?.length ?? 0;

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium capitalize">{placement.channel.replace('_', ' ')}</span>
          <span
            className={`text-xs rounded px-1.5 py-0.5 ${STATUS_STYLES[placement.status] ?? 'bg-muted'}`}
          >
            {placement.status}
            {placement.pauseReason ? ` (${placement.pauseReason})` : ''}
          </span>
        </div>
        <div className="flex gap-2">
          {(placement.status === 'draft' || placement.status === 'error') && (
            <button
              onClick={() => deploy.mutate(placement.id)}
              disabled={deploy.isPending}
              className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50"
            >
              Deploy (starts paused)
            </button>
          )}
          {placement.status === 'paused' && (
            <button
              onClick={() => lifecycle.mutate({ placementId: placement.id, verb: 'resume' })}
              disabled={lifecycle.isPending}
              className="px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs disabled:opacity-50"
            >
              Resume (go live)
            </button>
          )}
          {placement.status === 'live' && (
            <button
              onClick={() => lifecycle.mutate({ placementId: placement.id, verb: 'pause' })}
              disabled={lifecycle.isPending}
              className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
            >
              Pause
            </button>
          )}
        </div>
      </div>
      <div className="text-sm text-muted-foreground">
        {centsToUsd(placement.dailyBudgetCents)}/day · {keywordCount} keyword
        {keywordCount === 1 ? '' : 's'}
        {placement.externalCampaignRef ? ` · channel ref ${placement.externalCampaignRef}` : ''}
      </div>
      {placement.lastDeployError && (
        <div className="text-xs text-rose-600">{placement.lastDeployError}</div>
      )}
      {(deploy.isError || lifecycle.isError) && (
        <div className="text-xs text-rose-600">
          {((deploy.error ?? lifecycle.error) as Error | null)?.message ?? 'Action failed'}
        </div>
      )}
    </div>
  );
}

export function PlacementsSection({ holderId, campaignId, dailyBudgetCeilingCents }: Props) {
  const placementsQuery = useAdoptPlacements(holderId, campaignId);
  const [creating, setCreating] = useState(false);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">Channel placements</h2>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="text-sm text-primary hover:underline"
          >
            + New placement
          </button>
        )}
      </div>
      {creating && (
        <div className="rounded-lg border border-border p-4">
          <CreatePlacementForm
            holderId={holderId}
            campaignId={campaignId}
            dailyBudgetCeilingCents={dailyBudgetCeilingCents}
            onDone={() => setCreating(false)}
          />
        </div>
      )}
      {placementsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading placements…</p>
      ) : (placementsQuery.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No placements yet. A placement maps this campaign onto a connected ad account.
        </p>
      ) : (
        (placementsQuery.data ?? []).map((p) => (
          <PlacementRow key={p.id} placement={p} holderId={holderId} campaignId={campaignId} />
        ))
      )}
    </section>
  );
}
